import { useState, useEffect } from "react";

type PostSummary = {
  id: number;
  title: string;
  slug: string;
  published: 0 | 1;
  created_at: string;
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

type Props = {
  token: string;
  onEdit: (id: number) => void;
  onCreate: () => void;
  onLogout: () => void;
};

export function AdminPostList({ token, onEdit, onCreate, onLogout }: Props) {
  const [posts, setPosts] = useState<PostSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  async function load() {
    setError(null);
    try {
      const res = await fetch("/api/admin/posts", { headers });
      if (!res.ok) throw new Error();
      setPosts((await res.json()) as PostSummary[]);
    } catch {
      setError("Laden fehlgeschlagen.");
    }
  }

  useEffect(() => { load(); }, []);

  async function togglePublish(post: PostSummary) {
    await fetch(`/api/admin/posts/${post.id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ published: post.published ? 0 : 1 }),
    });
    load();
  }

  async function handleDelete(id: number, title: string) {
    if (!confirm(`"${title}" wirklich löschen?`)) return;
    await fetch(`/api/admin/posts/${id}`, { method: "DELETE", headers });
    load();
  }

  return (
    <div className="admin-page">
      <div className="noise-overlay" aria-hidden="true" />
      <div className="admin-wrap">
        <header className="admin-header">
          <h1 className="admin-title">Blog</h1>
          <div className="admin-header-actions">
            <button className="admin-btn" onClick={onCreate}>+ Neuer Beitrag</button>
            <button className="admin-btn admin-btn--ghost" onClick={onLogout}>Abmelden</button>
          </div>
        </header>

        {error && <p className="admin-error">{error}</p>}

        {!posts && !error && <p className="admin-muted">Lade…</p>}

        {posts && posts.length === 0 && (
          <p className="admin-muted">Noch keine Beiträge.</p>
        )}

        {posts && posts.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Titel</th>
                <th>Slug</th>
                <th>Status</th>
                <th>Datum</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.id}>
                  <td className="admin-td-title">{post.title}</td>
                  <td className="admin-td-mono">{post.slug}</td>
                  <td>
                    <span className={`admin-badge${post.published ? " admin-badge--published" : ""}`}>
                      {post.published ? "Veröffentlicht" : "Entwurf"}
                    </span>
                  </td>
                  <td className="admin-td-mono">{formatDate(post.created_at)}</td>
                  <td className="admin-td-actions">
                    <button
                      className="admin-btn admin-btn--ghost admin-btn--sm"
                      onClick={() => onEdit(post.id)}
                    >
                      Bearbeiten
                    </button>
                    <button
                      className="admin-btn admin-btn--ghost admin-btn--sm"
                      onClick={() => togglePublish(post)}
                    >
                      {post.published ? "Depublizieren" : "Veröffentlichen"}
                    </button>
                    <button
                      className="admin-btn admin-btn--danger admin-btn--sm"
                      onClick={() => handleDelete(post.id, post.title)}
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
